import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, CheckCircle2, ExternalLink, Code2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * WidgetPage — public marketing + install-instructions page for the
 * embeddable Priority Date Widget.
 *
 * Route: /widget
 *
 * Attorneys and immigration bloggers land here, pick a category+country,
 * see a LIVE preview of the badge, copy the snippet, and paste it on
 * their own site. Each embed drives a backlink to ds-260usa.com.
 */

const CATEGORIES = [
  "F1","F2A","F2B","F3","F4","EB1","EB2","EB3","EB3O","EB4","EB5",
];
const COUNTRIES = ["ALL","CHINA","INDIA","MEXICO","PHILIPPINES"];

function CopyBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  return (
    <div className="relative">
      <pre
        className="rounded-md p-4 text-[13px] font-mono overflow-x-auto"
        style={{ background: "#1F2937", color: "#E5E7EB" }}
        data-testid={testid}
      >
        {code}
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold"
        style={{ background: "#374151", color: "#E5E7EB" }}
        data-testid={`${testid}-copy`}
      >
        {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
      </button>
    </div>
  );
}

/** Live preview — actually loads the widget from our own domain. */
function WidgetPreview({ category, country, theme, cta }) {
  useEffect(() => {
    // Reset any previously-rendered widget preview so the change takes effect.
    // Uses replaceChildren() (safe DOM API) instead of innerHTML to avoid any
    // XSS surface — nothing user-controlled ever reaches string parsing.
    const container = document.getElementById("widget-preview-slot");
    if (!container) return;
    container.replaceChildren();
    const s = document.createElement("script");
    s.src = `${process.env.REACT_APP_BACKEND_URL}/api/widget/pd.js`;
    s.setAttribute("data-category", category);
    s.setAttribute("data-country", country);
    s.setAttribute("data-theme", theme);
    s.setAttribute("data-cta", cta ? "true" : "false");
    container.appendChild(s);
  }, [category, country, theme, cta]);
  return (
    <div
      className="p-6 rounded-lg border"
      style={{ background: "#F5F0E5", borderColor: "var(--border-stone)" }}
    >
      <div className="text-[10px] font-bold uppercase tracking-wide mb-2"
           style={{ color: "var(--text-secondary)" }}>
        Live preview (rendered by the actual widget)
      </div>
      <div id="widget-preview-slot"></div>
    </div>
  );
}


export default function WidgetPage() {
  const [category, setCategory] = useState("EB2");
  const [country, setCountry] = useState("INDIA");
  const [theme, setTheme] = useState("auto");
  const [cta, setCta] = useState(true);

  const scriptSrc = `${process.env.REACT_APP_BACKEND_URL}/api/widget/pd.js`;
  const snippet = `<script src="${scriptSrc}"
        data-category="${category}"
        data-country="${country}"${cta ? "" : `\n        data-cta="false"`}
        async></script>`;

  useEffect(() => {
    document.title = "Free Priority Date Widget for Attorneys & Bloggers — DS-260USA.com";
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="widget-page">
      <header
        className="border-b sticky top-0 z-10"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
            DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/visa-bulletin-checker" style={{ color: "var(--text-secondary)" }}>PD Checker</Link>
            <Link to="/faq" style={{ color: "var(--text-secondary)" }}>FAQ</Link>
            <Link to="/register" className="btn-primary text-sm px-4 py-2">Start a case</Link>
          </div>
        </div>
      </header>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="pill pill-ai mb-4 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Free · One line of code
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-3" style={{ color: "var(--text-primary)" }}>
            Priority Date Widget
          </h1>
          <p className="text-[16px] leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            A live, self-updating priority-date badge for any immigration attorney,
            law-firm blog, or immigration-focused website. Paste one line of code —
            your visitors see the current Visa Bulletin cutoff for any category+country,
            refreshed every month automatically.
          </p>
        </div>
      </section>

      {/* Builder + preview */}
      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-6 grid md:grid-cols-2 gap-8">
          {/* Builder */}
          <div className="card-surface p-6" style={{ background: "#FFFFFF" }}>
            <h2 className="font-display font-extrabold text-lg mb-4" style={{ color: "var(--text-primary)" }}>
              Configure your badge
            </h2>
            <div className="space-y-4">
              <div>
                <label className="field-label">Preference category</label>
                <select
                  className="field-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="widget-category"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Country of chargeability</label>
                <select
                  className="field-input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="widget-country"
                >
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="widget-cta-toggle">
                <input
                  type="checkbox"
                  checked={cta}
                  onChange={(e) => setCta(e.target.checked)}
                />
                <span style={{ color: "var(--text-primary)" }}>
                  Include "Check my priority date" link (recommended)
                </span>
              </label>
              <p className="text-[11px] italic" style={{ color: "var(--text-secondary)" }}>
                The badge auto-refreshes every time your visitors load the page.
                We fetch the latest Visa Bulletin on the 15th of every month.
              </p>
            </div>
          </div>

          {/* Live preview */}
          <div>
            <WidgetPreview category={category} country={country} theme={theme} cta={cta} />
          </div>
        </div>
      </section>

      {/* Snippet */}
      <section className="py-14 border-t" style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}>
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="font-display text-2xl font-extrabold mb-1" style={{ color: "var(--text-primary)" }}>
            <Code2 className="w-6 h-6 inline mr-2" />
            Copy this snippet
          </h2>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Paste it wherever you want the badge to appear — inside a blog post,
            a client-portal sidebar, a page footer, or anywhere else on your site.
          </p>
          <CopyBlock code={snippet} testid="widget-snippet" />
          <p className="text-[11px] mt-3" style={{ color: "var(--text-secondary)" }}>
            No accounts, no rate limits, no charge. Attribution stays with DS-260USA.com —
            the badge links back to us. That's the deal.
          </p>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/attorney-onepager.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "var(--action)" }}
            data-testid="widget-onepager-link"
          >
            <ExternalLink className="w-4 h-4" /> Sharing with your firm? Download the one-page PDF overview
          </a>
        </div>
      </section>

      {/* Instructions */}
      <section className="py-14" style={{ background: "var(--bg-app)" }}>
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="font-display text-2xl font-extrabold mb-4" style={{ color: "var(--text-primary)" }}>
            Installation cheat-sheet
          </h2>
          <div className="space-y-4 text-sm" style={{ color: "var(--text-secondary)" }}>
            <div className="card-surface p-4" style={{ background: "#FFFFFF" }}>
              <div className="font-display font-bold mb-1" style={{ color: "var(--text-primary)" }}>WordPress</div>
              Paste the snippet into a <b>Custom HTML block</b> (Gutenberg) or the <b>Text</b> tab of the Classic editor.
              For a site-wide badge (footer/sidebar), use Appearance → Widgets → <b>Custom HTML</b>.
            </div>
            <div className="card-surface p-4" style={{ background: "#FFFFFF" }}>
              <div className="font-display font-bold mb-1" style={{ color: "var(--text-primary)" }}>Squarespace / Wix</div>
              Add an <b>Embed / HTML block</b> and paste the snippet inside it.
            </div>
            <div className="card-surface p-4" style={{ background: "#FFFFFF" }}>
              <div className="font-display font-bold mb-1" style={{ color: "var(--text-primary)" }}>Static HTML / React / Next.js</div>
              Paste the snippet directly. In React or Next.js, place it inside a component that renders to HTML —
              the widget script self-initializes and doesn't require React state.
            </div>
            <div className="card-surface p-4" style={{ background: "#FFFFFF" }}>
              <div className="font-display font-bold mb-1" style={{ color: "var(--text-primary)" }}>Multiple badges on one page?</div>
              Paste the same snippet as many times as you want with different <code>data-category</code> and
              <code>data-country</code> values. Each script tag renders its own badge in-place.
            </div>
          </div>
        </div>
      </section>

      {/* Multiple example previews */}
      <section className="py-14 border-t" style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}>
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-display text-2xl font-extrabold mb-6" style={{ color: "var(--text-primary)" }}>
            Popular category examples
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { cat: "EB2", country: "INDIA", label: "EB-2 India" },
              { cat: "EB3", country: "CHINA", label: "EB-3 China" },
              { cat: "F4",  country: "PHILIPPINES", label: "F4 Philippines" },
              { cat: "F1",  country: "MEXICO", label: "F1 Mexico" },
              { cat: "F2A", country: "ALL", label: "F2A worldwide" },
              { cat: "EB5", country: "INDIA", label: "EB-5 India" },
            ].map((ex) => (
              <ExampleBadge key={`${ex.cat}-${ex.country}`} {...ex} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust footer */}
      <section className="py-14 border-t" style={{ background: "var(--bg-app)", borderColor: "var(--border-stone)" }}>
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-display text-2xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
            Why this exists
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Immigration attorneys and community bloggers waste hours every month manually
            updating priority-date charts. This widget does it for you — free, forever.
            The only ask: we keep the "Check my priority date → DS-260USA.com" link
            so your visitors can save their own case and get monthly emails.
          </p>
          <Link
            to="/visa-bulletin-checker"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "var(--action)" }}
          >
            Try the full public checker <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t py-8" style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}>
        <div className="mx-auto max-w-6xl px-6 flex flex-wrap gap-4 justify-between items-center text-xs" style={{ color: "var(--text-secondary)" }}>
          <div>© {new Date().getFullYear()} DS-260USA.com — Priority Date Widget</div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/" className="hover:opacity-80">Home</Link>
            <Link to="/visa-bulletin-checker" className="hover:opacity-80">PD Checker</Link>
            <Link to="/press-kit" className="hover:opacity-80">Press kit</Link>
            <Link to="/legal/privacy" className="hover:opacity-80">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ExampleBadge({ cat, country, label }) {
  useEffect(() => {
    const el = document.getElementById(`ex-${cat}-${country}`);
    if (!el) return;
    // replaceChildren() is a safe DOM API — nothing string-parsed, no XSS surface.
    el.replaceChildren();
    const s = document.createElement("script");
    s.src = `${process.env.REACT_APP_BACKEND_URL}/api/widget/pd.js`;
    s.setAttribute("data-category", cat);
    s.setAttribute("data-country", country);
    s.setAttribute("data-cta", "false");
    el.appendChild(s);
  }, [cat, country]);
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-secondary)" }}>
        {label}
      </div>
      <div id={`ex-${cat}-${country}`}></div>
    </div>
  );
}
